import { CellContext } from "@/lib/Components/DataTable/types";
import {
  BookingInclusionFieldsFragment,
  Currency,
  QueryRelocationsOrderByColumn,
  RelocationInclusionFieldsFragment,
  RelocationInclusionType,
  RelocationListQueryVariables,
  SortOrder,
} from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingChangeRelocationDialog } from "@/app/Bookings/Components/BookingChangeRelocationDialog";
import { Mark } from "@/components/catalyst/mark";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { inclusionIconMap } from "@/app/Relocations/Maps/inclusionIconMap";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Link } from "@/components/catalyst/link";
import { IconAndText } from "@/app/Relocations/Components/RelocationSummaryCard";
import { ArmchairIcon, CarIcon, RotateCwIcon } from "lucide-react";
import {
  ArrowTopRightOnSquareIcon,
  Cog8ToothIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import dayjs from "dayjs";
import { toast } from "sonner";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  RelocationListItem,
  relocationListQuery,
} from "@/app/Relocations/GraphQL/relocationListQuery";
import { relocationDeliveryOpenHoursQuery } from "@/app/Relocations/GraphQL/relocationOpenHoursQuery";
import {
  RelocationCost,
  relocationCostQuery,
} from "@/app/Relocations/GraphQL/relocationCostQuery";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/catalyst/badge";
import { mailBookingToSupplierMutation } from "@/app/Bookings/GraphQL/bookingMutations";
import { useBooking } from "@/app/Bookings/Hooks/useBooking";
import { Button } from "@/components/catalyst/button";

type MatchingRelocationCellId =
  | "relocation"
  | "supplier"
  | "vehicle"
  | "inclusions"
  | "cost"
  | "lines"
  | "actions";
export function BookingMatchingRelocationsTable({
  bookingId,
  onAssign,
  queryVariables,
  title,
}: {
  bookingId: string;
  onAssign?: (relocation: RelocationListItem) => void;
  queryVariables?: Partial<RelocationListQueryVariables>;
  title: string;
}) {
  const booking = useBooking(bookingId);

  const columns: DataTableColDef<
    RelocationListItem,
    unknown,
    MatchingRelocationCellId
  >[] = [
    {
      id: "relocation",
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Relocation" />
      ),
      cell: RelocationCell,
      meta: {
        booking,
      },
    },
    {
      id: "vehicle",
      header: "Vehicle",
      cell: VehicleCell,
      meta: {
        booking,
      },
    },
    {
      id: "inclusions",
      header: "Inclusions",
      cell: InclusionsCell,
      meta: {
        booking,
      },
    },
    {
      id: "cost",
      header: "Cost",
      cell: CostCell,
      meta: {
        booking,
      },
    },
    {
      id: "lines",
      header: "Plates",
      cell: LinesCell,
      meta: {
        booking,
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ReassignCell,
      meta: {
        booking,
        onAssign,
      },
    },
  ];

  return (
    <DataTable
      id="matching-relocations"
      columns={columns}
      document={relocationListQuery}
      title={title}
      getQueryVariables={({ sorting }) => {
        const orderBy: RelocationListQueryVariables["orderBy"] = sorting
          .map((value) => {
            const order = value.desc ? SortOrder.Desc : SortOrder.Asc;

            if (value.id === "relocation") {
              return {
                column: QueryRelocationsOrderByColumn.Id,
                order,
              };
            }
          })
          .filter(Boolean);

        return {
          page: 1,
          first: 200,
          orderBy,
          ...queryVariables,
        } satisfies RelocationListQueryVariables;
      }}
      accessor={(data) => data.relocations}
      searchable={false}
      columnToggles={false}
      paginated={false}
    />
  );
}

function RelocationCell({ row, column }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const booking: BookingRecord = (column.columnDef.meta as any)?.booking;

  const hasSameCities =
    booking.departureOffice.city.id === relocation.departureCity.id &&
    booking.deliveryOffice.city.id === relocation.deliveryCity.id;

  const hasSameSupplier = booking.supplier.id === relocation.supplier.id;

  const departAt = dayjs(booking.depart_at);
  const canPickup = departAt.isAfter(
    dayjs(row.original.available_from_date).startOf("day"),
  );
  const deliverAt = dayjs(booking.deliver_at);
  const canDropoff = deliverAt.isBefore(
    dayjs(row.original.available_to_date).endOf("day"),
  );

  const { data, isLoading } = useGqlQuery(relocationDeliveryOpenHoursQuery, {
    relocationId: relocation.id,
    departureDate: departAt.format("YYYY-MM-DD"),
    departureTime: departAt.format("HH:mm"),
  });

  const isDropoffOpenOnDeliveryDate = isLoading
    ? true
    : !!data?.relocationDeliveryOpenHours?.find((openHours) => {
        return openHours.date === deliverAt.format("YYYY-MM-DD");
      });

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-semibold">
        <Mark
          when={!hasSameCities}
          label={`Booking: ${booking.departureOffice.city.name} - ${booking.deliveryOffice.city.name}`}
        >
          <Link
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to={
              "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
            }
            params={(prev) => ({ ...prev, relocationId: relocation.id })}
            className="text-blue-600 hover:underline dark:text-blue-400"
            target={"_blank"}
          >
            <span>{relocation.departureCity.name}</span>
            <span className="text-zinc-400"> → </span>
            <span>{relocation.deliveryCity.name}</span>
            <ArrowTopRightOnSquareIcon
              className="ml-1 inline size-3.5 text-zinc-400"
              data-slot="icon"
            />
          </Link>
        </Mark>
      </p>
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        <Mark
          when={!hasSameSupplier}
          label={`Booking: ${booking.supplier.name}`}
        >
          {relocation.supplier.name}
        </Mark>
      </p>
      <p className="text-sm font-medium tabular-nums">
        <Mark
          when={!canPickup}
          label={`Pickup: ${formatDate(booking.depart_at)}`}
        >
          {formatDate(row.original.available_from_date)}
        </Mark>
        <span className="text-zinc-400">{" – "}</span>
        <Mark
          when={!canDropoff}
          label={`Dropoff: ${formatDate(booking.deliver_at)}`}
        >
          {formatDate(row.original.available_to_date)}
        </Mark>
      </p>
      {!isDropoffOpenOnDeliveryDate ? (
        <Badge color="yellow" className="gap-1">
          <ExclamationTriangleIcon className="size-3.5 shrink-0" />
          {formatDate(deliverAt)} unavailable for dropoff
        </Badge>
      ) : null}
    </div>
  );
}

function VehicleCell({ row, column }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const vehicle = relocation.vehicle;
  const t = useTranslations("vehicle");

  const booking: BookingRecord = (column.columnDef.meta as any)?.booking;

  const hasSameType = vehicle.type === booking.vehicle.type;
  const hasSameTransmission =
    vehicle.transmission === booking.vehicle.transmission;
  const hasSameSeatbelts = vehicle.seatbelts >= booking.vehicle.seatbelts;

  const hasSameVehicle = booking.vehicle.id === vehicle.id;

  const customerAge = dayjs(booking.depart_at).diff(
    booking.date_of_birth,
    "year",
  );

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-semibold">
        <Mark when={!hasSameVehicle} label={`Booking: ${booking.vehicle.name}`}>
          <Link
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to={"/$tenant/$orgId/supplier/{-$supplierId}/vehicles/$id"}
            params={(prev) => ({ ...prev, id: vehicle.id })}
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            {vehicle.name}
          </Link>
        </Mark>
      </p>
      <ul className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500 dark:text-zinc-400">
        <IconAndText
          Icon={CarIcon}
          text={t(`type.${vehicle.type}`)}
          highlight={!hasSameType}
          label={`Booking: ${t(`type.${booking.vehicle.type}`)}`}
        />
        <IconAndText
          Icon={Cog8ToothIcon}
          text={t(`transmission.${vehicle.transmission}`)}
          highlight={!hasSameTransmission}
          label={`Booking: ${t(`transmission.${booking.vehicle.transmission}`)}`}
        />
        <IconAndText
          Icon={ArmchairIcon}
          text={relocation.vehicle.seatbelts}
          highlight={!hasSameSeatbelts}
          label={`Booking: ${booking.vehicle.seatbelts} seats`}
        />
      </ul>

      {customerAge < relocation.vehicle.minimum_age ||
      customerAge > relocation.vehicle.maximum_age ? (
        <div className="flex flex-wrap gap-1">
          {customerAge < relocation.vehicle.minimum_age ? (
            <Badge color="red">
              Too young ({relocation.vehicle.minimum_age}+)
            </Badge>
          ) : null}

          {customerAge > relocation.vehicle.maximum_age ? (
            <Badge color="red">
              Too old (under {relocation.vehicle.maximum_age})
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function InclusionsCell({ row, column }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const booking: BookingRecord = (column.columnDef.meta as any)?.booking;

  const oldFuel = sumInclusionValue(
    booking?.inclusions ?? [],
    RelocationInclusionType.Fuel,
  );
  const newFuel = sumInclusionValue(
    relocation.inclusions,
    RelocationInclusionType.Fuel,
  );
  const isFuelDifferent = newFuel !== oldFuel;
  const hasMissingFuel = oldFuel > 0 && newFuel === 0;

  const oldFerry = sumInclusionValue(
    booking?.inclusions ?? [],
    RelocationInclusionType.Ferry,
  );
  const newFerry = sumInclusionValue(
    relocation.inclusions,
    RelocationInclusionType.Ferry,
  );
  const isFerryDifferent = newFerry !== oldFerry;
  const hasMissingFerry = oldFerry > 0 && newFerry === 0;

  return (
    <div className="space-y-1 text-sm">
      <ul className="space-y-1">
        {relocation.inclusions.map((inclusion) => {
          const Icon = inclusionIconMap[inclusion.type];
          const isFuelInclusion =
            inclusion.type === RelocationInclusionType.Fuel;
          const isFerryInclusion =
            inclusion.type === RelocationInclusionType.Ferry;
          const isDifferent =
            (isFuelInclusion && isFuelDifferent) ||
            (isFerryInclusion && isFerryDifferent);
          const bookingValue = isFuelInclusion
            ? oldFuel
            : isFerryInclusion
              ? oldFerry
              : 0;

          return (
            <li key={inclusion.id} className="flex items-center gap-2">
              <Icon className="h-4 w-4 shrink-0 text-yellow-500" />
              {inclusion.has_free_tank ? (
                <span className="font-semibold text-green-600">
                  One Free Tank
                </span>
              ) : (
                <Mark
                  when={isDifferent}
                  label={`Booking: ${formatCurrency(bookingValue, relocation.currency)}`}
                >
                  {formatCurrency(inclusion.value, relocation.currency)}
                </Mark>
              )}
              {inclusion.description ? (
                <Tooltip>
                  <TooltipTrigger>
                    <InfoCircledIcon className="h-4 w-4 text-yellow-500" />
                  </TooltipTrigger>
                  <TooltipContent className="p-2">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: inclusion.description ?? "--",
                      }}
                    />
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </li>
          );
        })}
      </ul>
      {hasMissingFuel ? (
        <p>
          <Mark
            when={true}
            label={`Booking: ${formatCurrency(oldFuel, relocation.currency)}`}
          >
            No fuel
          </Mark>
        </p>
      ) : null}
      {hasMissingFerry ? (
        <p>
          <Mark
            when={true}
            label={`Booking: ${formatCurrency(oldFerry, relocation.currency)}`}
          >
            No ferry
          </Mark>
        </p>
      ) : null}
    </div>
  );
}

function LinesCell({ row }: CellContext<RelocationListItem, any>) {
  const lines = row.original.lines;

  if (lines.length === 0) {
    return null;
  }

  return (
    <ul className="space-y-1 text-xs text-zinc-600 dark:text-zinc-400">
      {lines.map((line) => {
        const color = line.sold_at
          ? line.booking
            ? "bg-yellow-500"
            : "bg-red-500"
          : "bg-green-500";

        return (
          <li key={line.id} className="flex items-center gap-2">
            <span className={cn(color, "size-2 shrink-0 rounded-full")} />
            <span className="max-w-64 truncate" title={line.reference}>
              {line.reference}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function useRelocationCost(relocationId: string, booking: BookingRecord) {
  const departAt = dayjs(booking.depart_at);
  const deliverAt = dayjs(booking.deliver_at);

  return useGqlQuery(relocationCostQuery, {
    input: {
      relocation_id: relocationId,
      departure_date: departAt.format("YYYY-MM-DD"),
      departure_time: departAt.format("HH:mm"),
      delivery_date: deliverAt.format("YYYY-MM-DD"),
      delivery_time: deliverAt.format("HH:mm"),
      // Extras are tied to the booking's supplier, so they can't carry over to
      // another supplier's relocation — both sides price the hire alone.
      purchasable_extras: [],
    },
  });
}

function CostCell({ row, column }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const booking: BookingRecord = (column.columnDef.meta as any)?.booking;

  const { data, isPending } = useRelocationCost(relocation.id, booking);
  const { data: currentData } = useRelocationCost(
    booking.relocation.id,
    booking,
  );

  if (isPending) {
    return <Skeleton className="h-5 w-20" />;
  }

  if (!data) {
    return <span className="text-zinc-400">--</span>;
  }

  const cost = data.relocationCost.total_cost_amount;
  const current = currentData?.relocationCost;
  const isComparable = !!current && relocation.currency === booking.currency;
  const difference = isComparable ? cost - current.total_cost_amount : null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="space-y-1 text-left">
          <p className="text-sm font-semibold tabular-nums">
            {formatCurrency(cost, relocation.currency, { showZero: true })}
          </p>
          {difference === null ? null : (
            <p
              className={cn(
                "inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
                difference === 0
                  ? "bg-zinc-100 text-zinc-600 dark:bg-white/10 dark:text-zinc-300"
                  : difference > 0
                    ? "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-400"
                    : "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400",
              )}
            >
              {difference === 0
                ? "Same as current"
                : `${difference > 0 ? "+" : "-"}${formatCurrency(
                    Math.abs(difference),
                    relocation.currency,
                  )}`}
            </p>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="p-3">
        <CostBreakdown
          cost={data.relocationCost}
          currentCost={isComparable ? current : null}
          currency={relocation.currency}
        />
      </TooltipContent>
    </Tooltip>
  );
}

const costBreakdownLines: {
  label: string;
  getValue: (cost: RelocationCost) => number;
  isUnits?: boolean;
  isTotal?: boolean;
}[] = [
  {
    label: "Discounted units",
    getValue: (cost) => cost.discounted_units,
    isUnits: true,
  },
  {
    label: "Full price units",
    getValue: (cost) => cost.full_price_units,
    isUnits: true,
  },
  { label: "Booking fee", getValue: (cost) => cost.pay_now_booking_fee_amount },
  {
    label: "Holding deposit",
    getValue: (cost) => cost.pay_now_holding_deposit_amount,
  },
  {
    label: "Full price deposit",
    getValue: (cost) => cost.pay_now_full_price_units_deposit_amount,
  },
  {
    label: "Pay now",
    getValue: (cost) => cost.pay_now_total_amount,
    isTotal: true,
  },
  {
    label: "Discounted hire",
    getValue: (cost) => cost.pay_later_discounted_amount,
  },
  {
    label: "Full price hire",
    getValue: (cost) => cost.pay_later_full_price_amount,
  },
  {
    label: "Pay later",
    getValue: (cost) => cost.pay_later_total_amount,
    isTotal: true,
  },
  { label: "Refunded", getValue: (cost) => cost.total_refund_amount },
  {
    label: "Total",
    getValue: (cost) => cost.total_cost_amount,
    isTotal: true,
  },
];

function CostBreakdown({
  cost,
  currentCost,
  currency,
}: {
  cost: RelocationCost;
  currentCost: RelocationCost | null;
  currency: Currency;
}) {
  const format = (value: number, isUnits?: boolean) =>
    isUnits ? value : formatCurrency(value, currency, { showZero: true });

  return (
    <table className="text-xs">
      <thead>
        <tr className="text-left uppercase tracking-wide opacity-60">
          <th className="pb-1 pr-4 font-medium" />
          <th className="pb-1 pr-4 text-right font-medium">This</th>
          {currentCost ? (
            <th className="pb-1 pr-4 text-right font-medium">Current</th>
          ) : null}
          {currentCost ? (
            <th className="pb-1 text-right font-medium">Diff</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {costBreakdownLines.map((line) => {
          const value = line.getValue(cost);
          const currentValue = currentCost ? line.getValue(currentCost) : null;
          const difference =
            currentValue === null ? null : value - currentValue;

          return (
            <tr
              key={line.label}
              className={cn(
                "border-current/15 border-t",
                line.isTotal ? "font-semibold" : null,
              )}
            >
              <td className="whitespace-nowrap py-1 pr-4">{line.label}</td>
              <td className="py-1 pr-4 text-right tabular-nums">
                {format(value, line.isUnits)}
              </td>
              {currentValue === null ? null : (
                <td className="py-1 pr-4 text-right tabular-nums opacity-70">
                  {format(currentValue, line.isUnits)}
                </td>
              )}
              {difference === null ? null : (
                <td
                  className={cn(
                    "py-1 text-right tabular-nums",
                    difference === 0
                      ? "opacity-50"
                      : difference > 0
                        ? "text-red-400 dark:text-red-600"
                        : "text-green-400 dark:text-green-600",
                  )}
                >
                  {difference === 0
                    ? "--"
                    : `${difference > 0 ? "+" : "-"}${format(
                        Math.abs(difference),
                        line.isUnits,
                      )}`}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function sumInclusionValue(
  inclusions: (
    | RelocationInclusionFieldsFragment
    | BookingInclusionFieldsFragment
  )[],
  type: RelocationInclusionType,
) {
  return inclusions.reduce((sum, inclusion) => {
    return sum + (inclusion.type === type ? (inclusion.value ?? 0) : 0);
  }, 0);
}

function ReassignCell({ row, column }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const { openAsPromise } = useDialog(BookingChangeRelocationDialog);
  const booking: BookingRecord = (column.columnDef.meta as any).booking;
  const onAssign = (column.columnDef.meta as any)?.onAssign;
  const { mutateAsync: mailToSupplier } = useGqlMutation(
    mailBookingToSupplierMutation,
  );

  return (
    <Button
      LeadingIcon={RotateCwIcon}
      outline
      onClick={async () => {
        try {
          const newRelocation = await openAsPromise({
            booking,
            newRelocation: relocation,
          });
          onAssign?.(newRelocation);
          toast("Relocation assigned", {
            action: {
              label: "Email supplier",
              onClick: () => {
                toast.promise(
                  () =>
                    mailToSupplier({
                      id: booking.id,
                    }),
                  {
                    loading: `Sending pending email to ${relocation.supplier.name}`,
                    success: () => {
                      return `Email sent to supplier`;
                    },
                    error: "Error sending email",
                  },
                );
              },
            },
          });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          return;
        }
      }}
    >
      Change
    </Button>
  );
}
